import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { EditIdentity } from "../components/allowance/EditIdentity";
import { QuotaBar } from "../components/allowance/QuotaBar";
import { PageHeader } from "../components/shell/PageHeader";
import { ErrorNotice } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import { Datum, Metric, MicroLabel, SectionTitle } from "../components/ui/Text";
import { get } from "../lib/api";
import { dateOnly, kwh, percent, timestamp } from "../lib/format";
import type { IdentityDetail as Detail } from "../lib/types";

/**
 * One identity: what it is, whose it is, and the pool it draws on.
 *
 * ## The allowance is the holder's, not the card's
 *
 * A policy is assigned to a holder and shared by every card under it
 * (docs/identity-holders-plan.md §4). So the figure here is the *pool's*, the same
 * one every other card in it shows, and changing it happens on the holder's page —
 * which this page links to rather than offering a control that would act on
 * dozens of cards while looking like it acts on one.
 *
 * ## The two energy figures stay separate
 *
 * `committedWh` is what a chargeback export would bill. `inFlightWh` is a live
 * estimate from open sessions. Spec 6.3 keeps them distinct and so does this page —
 * they are never summed into one number, because one is auditable and the other is
 * a guess that will be revised when the session closes.
 *
 * ## Period boundaries are the depot's, not the browser's
 *
 * The period start and end are rendered in the depot's timezone. A calendar month
 * that begins at 17:00 UTC is not a bug; it is midnight where the chargers are.
 */
export function IdentityDetail() {
  const { identityId = "" } = useParams();
  const detail = useQuery({
    queryKey: ["identity", identityId] as const,
    queryFn: () => get<Detail>(`/identities/${encodeURIComponent(identityId)}`),
    enabled: identityId !== "",
  });

  if (detail.isError) {
    return (
      <>
        <PageHeader eyebrow="Identity" title="Not available" />
        <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
        <BackLink />
      </>
    );
  }

  const row = detail.data;
  const period = row?.currentPeriod ?? null;

  return (
    <>
      <PageHeader
        eyebrow={
          row === undefined ? "Identity" : `${row.tokenType === "vin" ? "VIN" : "RFID"} · ${row.idTag}`
        }
        title={row?.label ?? "Identity"}
        metrics={
          period === null ? undefined : (
            <>
              <Metric
                label="Committed"
                value={kwh(period.committedWh)}
                unit="kWh"
                hint={`${period.closedSessionCount} closed session${period.closedSessionCount === 1 ? "" : "s"}`}
              />
              <Metric
                label="In flight"
                value={kwh(period.inFlightWh)}
                unit="kWh"
                hint="live estimate, not billable"
                tone="muted"
              />
              <Metric
                label="Of quota"
                value={percent(period.consumedPct)}
                hint={`${kwh(period.quotaWh)} kWh, shared`}
                tone={(period.consumedPct ?? 0) >= 100 ? "signal" : "ink"}
              />
            </>
          )
        }
        actions={
          row === undefined ? undefined : (
            <Pill tone={row.status === "Accepted" ? "available" : "faulted"}>{row.status}</Pill>
          )
        }
      />

      {row === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <>
          <section className="flex flex-col gap-4">
            <SectionTitle
              action={
                /**
                 * The sessions behind the figure. A committed total with no way to
                 * see what it is made of is not auditable, and "which trip was
                 * that 40 kWh" is the first question anyone asks of a chargeback.
                 */
                <Link
                  to={`/sessions?identityId=${encodeURIComponent(row.id)}`}
                  className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
                >
                  Its sessions
                </Link>
              }
            >
              Current period
            </SectionTitle>
            {period === null ? (
              <p className="m-0 max-w-prose text-body text-ink-muted">
                No allowance governs {row.holder.name} or any group above it, so there is no period
                to measure against. Energy is still recorded against this card.
              </p>
            ) : (
              <>
                <QuotaBar
                  committedWh={period.committedWh}
                  inFlightWh={period.inFlightWh}
                  quotaWh={period.quotaWh}
                  thresholds={row.allowance?.policy.warnThresholds ?? []}
                />
                <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
                  <Datum label="Pool">
                    <HolderLink id={period.holder.id} name={period.holder.name} />
                  </Datum>
                  <Datum label="Period">{period.label}</Datum>
                  <Datum label="Opened">
                    {timestamp(period.periodStart, POLICY_ZONE_NOTE.zone)}
                  </Datum>
                  <Datum label="Closes">{timestamp(period.periodEnd, POLICY_ZONE_NOTE.zone)}</Datum>
                  <Datum label="Closed sessions">{period.closedSessionCount}</Datum>
                </dl>
                <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
                  {POLICY_ZONE_NOTE.text}
                </p>
              </>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <SectionTitle>Allowance</SectionTitle>

            {row.allowance === null ? (
              <p className="m-0 max-w-prose text-body text-ink-muted">
                None. Assign one on <HolderLink id={row.holder.id} name={row.holder.name} />, or on a
                group above it.
              </p>
            ) : (
              <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
                <Datum label="Policy">{row.allowance.policy.name}</Datum>
                <Datum label="Assigned to">
                  <HolderLink id={row.allowance.holder.id} name={row.allowance.holder.name} />
                </Datum>
                <Datum label="Quota">{kwh(row.allowance.policy.quotaWh)} kWh, shared</Datum>
                <Datum label="Period type">
                  {row.allowance.policy.periodType.replace(/_/g, " ")}
                </Datum>
                <Datum label="Assigned from">
                  {dateOnly(row.allowance.policy.effectiveFrom, POLICY_ZONE_NOTE.zone)}
                </Datum>
                <Datum label="Warn at">
                  {row.allowance.policy.warnThresholds.map((value) => `${value}%`).join(", ")}
                </Datum>
                <Datum label="Concurrent sessions">
                  {row.allowance.policy.allowConcurrentTx ? "Allowed" : "One at a time"}
                </Datum>
              </dl>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <SectionTitle>Card</SectionTitle>
            <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
              <Datum label="ID tag" mono>
                {row.idTag}
              </Datum>
              <Datum label="Token">{row.tokenType === "vin" ? "Vehicle VIN" : "RFID card"}</Datum>
              <Datum label="Holder">
                <HolderLink id={row.holder.id} name={row.holder.name} />
              </Datum>
              <Datum label="Expires">
                {row.expiryDate === null
                  ? "No expiry"
                  : dateOnly(row.expiryDate, POLICY_ZONE_NOTE.zone)}
              </Datum>
              <Datum label="First seen">{dateOnly(row.createdAt, POLICY_ZONE_NOTE.zone)}</Datum>
              {/* Every session ever, not this period's. It is what decides whether
                  the card can be removed at all, so it belongs next to the control
                  that acts on it rather than only in the figures above. */}
              <Datum label="Sessions recorded">{row.sessionCount}</Datum>
            </dl>

            {/* Renaming and removal live below the facts they change. The card's
                label is personal data (spec 9) and goes stale the way any name
                does — a van changes hands, a contractor's card is reissued — so
                correcting it has to be reachable without SQL. */}
            <EditIdentity identity={row} />
          </section>

          <BackLink />
        </>
      )}
    </>
  );
}

function HolderLink({ id, name }: { readonly id: string; readonly name: string }) {
  return (
    <Link
      to={`/holders/${encodeURIComponent(id)}`}
      className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      {name}
    </Link>
  );
}

function BackLink() {
  return (
    <Link
      to="/identities"
      className="self-start text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      Back to identities
    </Link>
  );
}

/**
 * The server resolves a pool's period in the *first* depot's timezone, since a
 * holder is not depot-scoped (spec 5) and a card used across two zones is
 * genuinely ambiguous until Phase 3 writes explicit bounds per session.
 *
 * This page therefore states the limitation instead of formatting the timestamps
 * in a zone it cannot verify. UTC is used for display because it is the one zone
 * that is never silently wrong — every deployment today is single-zone, so the
 * caption is what closes the gap rather than a guess at the offset.
 */
const POLICY_ZONE_NOTE = {
  zone: "UTC",
  text: "Times shown in UTC. Period boundaries are drawn in the depot's timezone, so a calendar month may open at an hour that is not midnight here.",
} as const;
